<template>
  <div v-show="show" class="sanne-step safe-area">
    <!-- header: Skip (left), STEP n OF total (centre), close (right) -->
    <div class="sanne-step-header">
      <span class="sanne-step-skip" @click="$emit('skip')">{{ $t('scanscreen.skip') }}</span>
      <span class="sanne-step-count">{{ $t('sanne.tutorial_step', { n: step, total }) }}</span>
      <img class="sanne-step-close" :src="closeIcon" alt="Leave" @click="$emit('leave')" />
    </div>
    <div class="sanne-step-body">
      <h1 class="sanne-step-title">{{ title }}</h1>
      <img class="sanne-step-photo" :src="photo" alt="" />
      <div class="sanne-step-dots">
        <span v-for="n in total" :key="n" :class="{ active: n === step }" />
      </div>
      <c-button @click="next">
        <span>{{ $t('mobileScan.next') }}</span>
      </c-button>
    </div>
  </div>
</template>

<script>
import audioPlayer from '@/mixins/audio-player';
import CButton from '@/components/common/CButton.vue';
import closeIcon from '@/assets/sanne/icons/close-icon.svg';

/**
 * One page of the Sanne "before the scan" tutorial (Figma Tutorial section
 * 5698:14343, mobile 5697:11681): a photo card in place of the default lottie,
 * a step counter, page dots and a Next button. Unlike the default pages
 * (auto-next), the voice-over only narrates the step: the page never advances
 * on its own, the guest moves on with Next, Skip or the close icon.
 */
export default {
  name: 'SanneStep',
  components: {
    CButton,
  },
  mixins: [audioPlayer],
  props: {
    show: Boolean,
    step: Number,
    total: {
      type: Number,
      default: 3,
    },
    title: String,
    photo: String,
    audio: String,
  },
  data() {
    return {
      closeIcon,
    };
  },
  methods: {
    next() {
      this.$emit('next');
    },
  },
  watch: {
    show: {
      handler(nv, ov) {
        if (nv && !ov) {
          this.playAudio(this.audio, true);
        } else if (!nv && ov) {
          // the steps are v-show siblings, each with its own player: cut this
          // step's voice-over here when Next comes early, or it keeps playing
          // under the next step's
          this.stopAudio();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.sanne-step {
  @apply fixed top-0 left-0 w-full h-full flex flex-col bg-surface;
  color: var(--color-text);
}

.sanne-step-header {
  @apply relative flex items-end justify-between w-full flex-shrink-0;
  // Figma frames carry the status bar (44 on phones, 24 on iPad) and positions
  // are taken from the safe area, like every Sanne screen: the 117 header
  // becomes 73 (close icon 17 under the safe area, title at 72); on iPad the
  // icon sits at safe area + 20 (Shared Layout Rules), so the header is 76
  height: 73px;
  padding: 0 36px 26px;
  @screen lg {
    @apply relative z-10; // above the body, which spans the whole screen on iPad
    height: 76px;
    padding: 0 72px 26px;
  }
}

.sanne-step-skip {
  font-family: var(--font-heading);
  font-size: 16px;
  line-height: 32px;
  letter-spacing: -0.4px;
  color: var(--color-primary);
  text-decoration: underline;
  cursor: pointer;
  @screen lg {
    font-size: 20px;
  }
}

// centred on the screen, on the same line as Skip and the close icon
.sanne-step-count {
  @apply absolute left-0 right-0 mx-auto text-center pointer-events-none;
  bottom: 31px;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.2px;
  @screen lg {
    bottom: 32px;
    font-size: 16px;
  }
}

.sanne-step-close {
  width: 30px;
  height: 30px;
  cursor: pointer;
}

.sanne-step-body {
  @apply flex flex-col items-center flex-1 mx-auto;
  width: 320px;
  padding-bottom: calc(24px + var(--safe-bottom));
  @screen lg {
    @apply absolute inset-0 justify-center;
    width: 480px;
    padding-bottom: 0;
  }
}

// fixed-height title box (Figma: 96 on phones, 94 on iPad) so the photo, dots
// and button stay put while the copy changes length between pages
.sanne-step-title {
  @apply flex items-center justify-center;
  min-height: 96px;
  font-family: var(--font-heading);
  font-size: 20px;
  line-height: 24px;
  letter-spacing: -0.4px;
  text-align: center;
  @screen lg {
    min-height: 94px;
    font-size: 24px;
    line-height: 28px;
  }
}

.sanne-step-photo {
  width: 256px;
  height: 427px;
  margin-top: 24px;
  border-radius: 12px;
  object-fit: cover;
  @screen lg {
    width: 360px;
    height: 600px;
    margin-top: 60px;
  }
}

.sanne-step-dots {
  @apply flex justify-center;
  gap: 6px;
  margin: 24px 0;
  @screen lg {
    margin-top: 60px;
  }
  span {
    width: 22px;
    height: 4px;
    border-radius: 2px;
    background: var(--color-text);
    opacity: 0.32;
    &.active {
      opacity: 1;
    }
  }
}
</style>
